import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  claimSelfRegisterIntake,
  postCustomerFromApp,
} from '../../utils/registration/postCustomerFromApp';
import { saveCustomerLoginPhone } from '../../utils/login/savedPhone';
import BirthdayPickerEn from '../../components/ui/BirthdayPickerEn';
import PasswordInput from '../../components/PasswordInput';
import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import { STORE_DISPLAY_NAME } from '../../constants/branding';
import { syncPushSubscriptionForCustomer } from '../../utils/pushNotifications';
import { saveCustomerAuthToken } from '../../utils/auth/customerAuthToken';
import {
  CUSTOMER_NATIONALITY_OPTIONS,
  type CustomerNationalityOption,
} from '../../constants/customerNationalityOptions';

const MIN_PASSWORD_LENGTH = 8;

type Gender = 'male' | 'female' | 'other' | '';

type Province =
  | 'Phnom Penh'
  | 'Banteay Meanchey'
  | 'Battambang'
  | 'Kampong Cham'
  | 'Kampong Chhnang'
  | 'Kampong Speu'
  | 'Kampong Thom'
  | 'Kampot'
  | 'Kandal'
  | 'Koh Kong'
  | 'Kratie'
  | 'Mondulkiri'
  | 'Oddar Meanchey'
  | 'Pailin'
  | 'Preah Sihanouk'
  | 'Preah Vihear'
  | 'Prey Veng'
  | 'Pursat'
  | 'Ratanakiri'
  | 'Siem Reap'
  | 'Stung Treng'
  | 'Svay Rieng'
  | 'Takeo'
  | 'Tbong Khmum'
  | '';

type PhnomPenhDistrict =
  | '7 Makara'
  | 'Boeng Keng Kang'
  | 'Daun Penh'
  | 'Chbar Ampov'
  | 'Chamkar Mon'
  | 'Chroy Changvar'
  | 'Dangkao'
  | 'Mean Chey'
  | 'Prampi Makara'
  | 'Por Senchey'
  | 'Russey Keo'
  | 'Sen Sok'
  | 'Tuol Kouk'
  | '';

type Channel =
  | 'other'
  | 'facebook'
  | 'instagram'
  | 'referral'
  | 'tiktok'
  | 'phone'
  | 'telegram';

const CHANNELS: Channel[] = [
  'other',
  'facebook',
  'instagram',
  'referral',
  'tiktok',
  'phone',
  'telegram',
];

const PROVINCES: Province[] = [
  'Phnom Penh',
  'Banteay Meanchey',
  'Battambang',
  'Kampong Cham',
  'Kampong Chhnang',
  'Kampong Speu',
  'Kampong Thom',
  'Kampot',
  'Kandal',
  'Koh Kong',
  'Kratie',
  'Mondulkiri',
  'Oddar Meanchey',
  'Pailin',
  'Preah Sihanouk',
  'Preah Vihear',
  'Prey Veng',
  'Pursat',
  'Ratanakiri',
  'Siem Reap',
  'Stung Treng',
  'Svay Rieng',
  'Takeo',
  'Tbong Khmum',
];

const PHNOM_PENH_DISTRICTS: PhnomPenhDistrict[] = [
  '7 Makara',
  'Boeng Keng Kang',
  'Daun Penh',
  'Chbar Ampov',
  'Chamkar Mon',
  'Chroy Changvar',
  'Dangkao',
  'Mean Chey',
  'Prampi Makara',
  'Por Senchey',
  'Russey Keo',
  'Sen Sok',
  'Tuol Kouk',
];

type FormState = {
  last_name: string;
  first_name: string;
  tel: string;
  password: string;
  password_confirm: string;
  gender: Gender;
  birthday: string; // 'YYYY-MM-DD'
  nationality: string;
  province: Province;
  district: PhnomPenhDistrict | '';
  channel: Channel;
};

const RegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceFromQuery = searchParams.get('source');
  const storeIdFromQuery = Number(searchParams.get('storeId'));
  const reservationIdFromQuery = Number(searchParams.get('reservationId'));
  const customerIdFromQuery = Number(searchParams.get('customerId'));
  const sessionIdFromQuery = Number(searchParams.get('selfRegisterSessionId'));
  const isAdminQrSource = sourceFromQuery === 'admin_qr';
  const resolvedStoreId =
    Number.isFinite(storeIdFromQuery) && storeIdFromQuery > 0
      ? storeIdFromQuery
      : null;
  const resolvedReservationId =
    Number.isFinite(reservationIdFromQuery) && reservationIdFromQuery > 0
      ? reservationIdFromQuery
      : null;
  const resolvedCustomerId =
    Number.isFinite(customerIdFromQuery) && customerIdFromQuery > 0
      ? customerIdFromQuery
      : null;
  const resolvedSessionId =
    Number.isFinite(sessionIdFromQuery) && sessionIdFromQuery > 0
      ? sessionIdFromQuery
      : null;

  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimReady, setClaimReady] = useState(!isAdminQrSource);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [claimedSessionId, setClaimedSessionId] = useState<number | null>(null);
  const [nationalityPreset, setNationalityPreset] = useState<'' | CustomerNationalityOption>('');
  const [form, setForm] = useState<FormState>({
    last_name: '',
    first_name: '',
    tel: '',
    password: '',
    password_confirm: '',
    gender: '',
    birthday: '',
    nationality: '',
    province: 'Phnom Penh',
    district: '',
    channel: 'other',
  });

  useEffect(() => {
    const telFromQuery = searchParams.get('tel');
    if (telFromQuery) {
      setForm((prev) => ({ ...prev, tel: telFromQuery }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isAdminQrSource) {
      setClaimReady(true);
      return;
    }
    if (!resolvedStoreId) {
      setClaimReady(false);
      toast.error('Store information is missing from this QR code.');
      return;
    }

    let mounted = true;
    const run = async () => {
      setClaiming(true);
      try {
        const result = await claimSelfRegisterIntake({
          source: 'admin_qr',
          store_id: resolvedStoreId,
          self_register_session_id: resolvedSessionId,
        });
        if (!mounted) return;
        setClaimedSessionId(result.self_register_session_id);
        setClaimReady(true);
      } catch (error: any) {
        if (!mounted) return;
        setClaimReady(false);
        if (error?.response?.status === 409) {
          toast.error('Registration intake has expired. Please ask staff to restart intake.');
        } else {
          toast.error('Failed to start registration intake. Please ask staff for help.');
        }
      } finally {
        if (mounted) setClaiming(false);
      }
    };

    void run();
    return () => {
      mounted = false;
    };
  }, [isAdminQrSource, resolvedSessionId, resolvedStoreId]);

  const handleChange = (
    field: keyof FormState,
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!form.last_name || !form.first_name) {
      setSubmitError('Please enter your family name and given name.');
      toast.warning('Please enter your family name and given name.');
      return;
    }
    if (!form.tel) {
      setSubmitError('Phone number is required.');
      toast.warning('Phone number is required.');
      return;
    }
    if (form.password.trim().length < MIN_PASSWORD_LENGTH) {
      setSubmitError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      toast.warning(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (form.password !== form.password_confirm) {
      setSubmitError('Password confirmation does not match.');
      toast.warning('Password confirmation does not match.');
      return;
    }
    if (isAdminQrSource && !claimedSessionId) {
      setSubmitError('Registration intake is not active. Please ask staff to restart intake.');
      toast.error('Registration intake is not active. Please ask staff to restart intake.');
      return;
    }
  
    setSaving(true);
    try {
      const payload = {
        last_name: form.last_name,
        first_name: form.first_name,
        tel: form.tel,
        password: form.password,
        gender: form.gender || null,
        birthday: form.birthday || null,
        nationality: resolvedNationality || null,
        province: form.province || null,
        district: form.province === 'Phnom Penh' ? form.district || null : null,
        channel: form.channel || 'other',
        source: sourceFromQuery || null,
        store_id: resolvedStoreId,
        self_register_session_id: claimedSessionId,
      };
  
      const { customer_id, name, auth_token } = await postCustomerFromApp(payload);

      saveCustomerAuthToken(auth_token);
      localStorage.setItem('customerId', String(customer_id));
      localStorage.setItem('customerName', name || `${form.first_name}`);
      saveCustomerLoginPhone(form.tel);
      void syncPushSubscriptionForCustomer(customer_id).catch((error) => {
        console.error('Failed to sync push subscription after registration:', error);
      });

      toast.success('Registration completed!');
      navigate('/reserve');
    } catch (error: any) {
      console.error(error);
      if (error.response?.status === 409) {
        const msg = isAdminQrSource
          ? 'This phone number is already used by another customer. Please check with staff.'
          : 'This phone number is already registered.';
        setSubmitError(msg);
        toast.error(msg);
      } else if (error.response?.status === 400) {
        const msg = error.response?.data?.error ?? 'Invalid input. Please check your form.';
        setSubmitError(msg);
        toast.error(msg);
      } else {
        const msg = 'Failed to register. Please try again.';
        setSubmitError(msg);
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const isPhnomPenh = form.province === 'Phnom Penh';
  const resolvedNationality = nationalityPreset || '';
  const fieldClass =
    'w-full rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100';
  const sectionClass = 'space-y-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 px-4 py-6">
      <main className="mx-auto max-w-lg">
        <section className="overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-lg shadow-emerald-100/60">
          <div className="border-b border-emerald-200 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 px-5 py-5 text-white">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-11 w-11 overflow-hidden rounded-full border border-white/40 bg-white shadow-sm">
                <img
                  src={camiyuiLogo}
                  alt={STORE_DISPLAY_NAME}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-100">{STORE_DISPLAY_NAME}</p>
                <h1 className="text-lg font-semibold">Customer Registration</h1>
              </div>
            </div>
            <p className="text-xs text-emerald-50/90">
              Please complete your profile to start online booking.
            </p>
            {isAdminQrSource && resolvedReservationId && (
              <div className="mt-2 space-y-1 text-xs font-semibold text-emerald-100">
                <p>Reservation ID: {resolvedReservationId}</p>
                {resolvedCustomerId && <p>Customer ID: {resolvedCustomerId}</p>}
              </div>
            )}
          </div>

          <div className="space-y-6 p-5">
            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Name <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Family name"
                  className={fieldClass}
                  value={form.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Given name"
                  className={fieldClass}
                  value={form.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                />
              </div>
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Phone Number <span className="text-rose-500">*</span>
              </label>
              <input
                type="tel"
                className={fieldClass}
                placeholder="012 345 678"
                value={form.tel}
                onChange={(e) => handleChange('tel', e.target.value)}
              />
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Password <span className="text-rose-500">*</span>
              </label>
              <div className="space-y-3">
                <PasswordInput
                  className={`${fieldClass} pr-14`}
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  autoComplete="new-password"
                />
                <PasswordInput
                  className={`${fieldClass} pr-14`}
                  placeholder="Confirm password"
                  value={form.password_confirm}
                  onChange={(e) => handleChange('password_confirm', e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-500">
                You will use this password together with your phone number whenever you log in.
              </p>
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Gender</label>
              <div className="grid grid-cols-3 gap-2">
                {(['male', 'female', 'other'] as const).map((g) => {
                  const active = form.gender === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      className={[
                        'rounded-xl border px-2 py-2 text-sm font-medium transition',
                        active
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-emerald-200 bg-white text-slate-700 hover:bg-emerald-50',
                      ].join(' ')}
                      onClick={() => handleChange('gender', g)}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={sectionClass}>
              <BirthdayPickerEn
                label="Birthday (optional)"
                value={form.birthday}
                onChange={(v) => setForm((prev) => ({ ...prev, birthday: v }))}
                min="1900-01-01"
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                Nationality (optional)
              </label>
              <select
                className={fieldClass}
                value={nationalityPreset}
                onChange={(e) => {
                  const v = e.target.value as '' | CustomerNationalityOption;
                  setNationalityPreset(v);
                  handleChange('nationality', v);
                }}
              >
                <option value="">Select nationality</option>
                {CUSTOMER_NATIONALITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Province</label>
              <select
                className={fieldClass}
                value={form.province}
                onChange={(e) => handleChange('province', e.target.value as Province)}
              >
                <option value="">Select province</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              {isPhnomPenh && (
                <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">
                  District (Phnom Penh)
                </label>
                <select
                  className={fieldClass}
                  value={form.district}
                  onChange={(e) => handleChange('district', e.target.value as PhnomPenhDistrict)}
                >
                  <option value="">Select district</option>
                  {PHNOM_PENH_DISTRICTS.map((d) => (
                    <option key={d} value={d}>
                    {d}
                  </option>
                ))}
                </select>
                </div>
              )}
            </div>

            <div className={sectionClass}>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                How did you find us?
              </label>
              <select
                className={fieldClass}
                value={form.channel}
                onChange={(e) => handleChange('channel', e.target.value as Channel)}
              >
                {CHANNELS.map((ch) => (
                  <option key={ch} value={ch}>
                    {ch.charAt(0).toUpperCase() + ch.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {submitError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {submitError}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || claiming || (isAdminQrSource && !claimReady)}
              className={[
                'w-full rounded-xl py-3 text-sm font-semibold transition',
                saving || claiming || (isAdminQrSource && !claimReady)
                  ? 'cursor-not-allowed bg-slate-300 text-slate-500'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700',
              ].join(' ')}
            >
              {saving ? 'Saving...' : claiming ? 'Preparing registration...' : 'Register and Start Booking'}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RegistrationPage;
